import type { MaintenanceQuestionnaireCandidate, MaintenanceQuestionnaireExpected, MaintenanceQuestionnaireInventory } from '@course-authoring/shared-types';
import { fetchWithAuthRetry } from './apiUtils';
import { MaintenanceApiError } from './maintenanceRepublishApi';
import { publishQuestionnaire, type QuestionnaireWarning } from './questionnaireApi';
import { checkCurrentAuthoringBuild } from './currentAuthoringBuild';
import { isFatalPublishingError } from './courseDeployApi';

const base = `${import.meta.env.VITE_API_BASE_URL || '/api'}/admin/republish/questionnaires`;
export const questionnaireItemKey = (item: Pick<MaintenanceQuestionnaireCandidate, 'id' | 'accountId'>) => `questionnaire:${item.id}:${item.accountId}`;
async function read<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { message?: string; code?: string };
    throw new MaintenanceApiError(body.message ?? `Questionnaire request failed (${response.status}).`, response.status, body.code);
  }
  return response.json() as Promise<T>;
}
export async function fetchMaintenanceQuestionnaires(page = 1, cutoff?: string, filterByAge = true): Promise<MaintenanceQuestionnaireInventory> {
  const query = new URLSearchParams({ page: String(page), filterByAge: String(filterByAge) });
  if (cutoff) query.set('cutoff', cutoff);
  return read(await fetchWithAuthRetry(`${base}?${query}`));
}
export interface QuestionnaireMaintenanceOutcome {
  item: MaintenanceQuestionnaireCandidate;
  status: 'pending' | 'running' | 'completed' | 'skipped' | 'failed';
  message?: string;
  totalMs?: number;
}
export function orderMaintenanceQuestionnaires(items: MaintenanceQuestionnaireCandidate[]) {
  return [...new Map(items.map(item => [questionnaireItemKey(item), item])).values()].sort((a, b) =>
    Date.parse(a.deployedAt) - Date.parse(b.deployedAt) || a.id - b.id || a.accountId - b.accountId);
}

export async function runQuestionnaireMaintenanceBatch(items: MaintenanceQuestionnaireCandidate[], options: {
  signal: AbortSignal;
  onOutcome: (outcome: QuestionnaireMaintenanceOutcome) => void;
  onItemSettled: () => Promise<unknown>;
}) {
  let stopReason: string | undefined;
  for (const item of orderMaintenanceQuestionnaires(items)) {
    if (options.signal.aborted) return;
    if (stopReason) { options.onOutcome({ item, status: 'skipped', message: stopReason }); continue; }
    const started = performance.now();
    try {
      const build = await checkCurrentAuthoringBuild();
      if (options.signal.aborted) return;
      if (build.status !== 'current') {
        stopReason = 'The current browser build could not be confirmed. Reload before starting another batch.';
        options.onOutcome({ item, status: 'skipped', message: stopReason });
        continue;
      }
      options.onOutcome({ item, status: 'running' });
      let expected: MaintenanceQuestionnaireExpected | undefined;
      const result = await publishQuestionnaire({ ...item, id: String(item.id), lifecycle: 'publish' }, {
        prepare: async () => {
          const prepared = await read<{ expected: MaintenanceQuestionnaireExpected; documents: Record<string, unknown>; warnings?: QuestionnaireWarning[] }>(
            await fetchWithAuthRetry(`${base}/${item.id}/prepare`, { method: 'POST', body: JSON.stringify({ accountId: item.accountId }), signal: options.signal }));
          expected = prepared.expected;
          return prepared;
        },
        deploy: packageBase64 => fetchWithAuthRetry(`${base}/${item.id}/deliver`, {
          method: 'POST', body: JSON.stringify({ packageBase64, accountSlug: expected?.accountSlug, expected }), signal: options.signal,
        }),
      });
      options.onOutcome({ item, status: 'completed', message: result.warnings.map(warning => warning.reason).join(' · ') || undefined, totalMs: performance.now() - started });
    } catch (error) {
      if (options.signal.aborted) return;
      if (isFatalPublishingError(error)) stopReason = 'Administrator access was lost. Sign in with an authorized account before retrying.';
      options.onOutcome({ item, status: 'failed', message: stopReason ?? (error instanceof Error ? error.message : 'Questionnaire republish failed.'), totalMs: performance.now() - started });
    }
    if (!options.signal.aborted) await options.onItemSettled();
  }
}
